import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { IndianRupee, Plus, Trash2 } from "lucide-react";

const vehicleModels = [
  "Kinetic Safar Smart",
  "Kinetic Safar Jumbo",
  "Kinetic Safar Star",
  "Kinetic Safar Shakti",
  "Kinetic E-Luna",
  "Kinetic Zing HSS",
];

const serviceTypes = [
  "Regular Service",
  "Free Service",
  "Battery Service",
  "Motor Repair",
  "Controller Repair",
  "Charger Repair",
  "Body Repair",
  "Accident Repair",
  "General Checkup",
  "Other",
];

const paymentModes = [
  "Cash",
  "Bank Transfer",
  "UPI",
  "Cheque",
];

// ── Helpers outside component to prevent input remount on every keystroke ──
const handlePriceKeyDown = (e) => {
  const ctrl = e.ctrlKey || e.metaKey;
  if (ctrl) return;
  const pass = ["Backspace","Delete","Tab","ArrowLeft","ArrowRight","Home","End"];
  if (pass.includes(e.key)) return;
  if (e.key === "." && !e.target.value.includes(".")) return;
  if (/^\d$/.test(e.key)) return;
  e.preventDefault();
};

const handleDigitsKeyDown = (e) => {
  const ctrl = e.ctrlKey || e.metaKey;
  if (ctrl) return;
  const pass = ["Backspace","Delete","Tab","ArrowLeft","ArrowRight","Home","End"];
  if (pass.includes(e.key)) return;
  if (/^\d$/.test(e.key)) return;
  e.preventDefault();
};
// ──────────────────────────────────────────────────────────────────────────────


const ServiceBillForm = ({
  formData,
  onInputChange,
  onSelectChange,
  onServiceItemChange,
  onPartsItemChange,
  addServiceItem,
  removeServiceItem,
  addPartsItem,
  removePartsItem,
  calculateTotal
}) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };
  
  return (
    <div className="space-y-8">
      {/* Document Info */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Bill Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="billNumber">Bill Number</Label>
            <Input
              id="billNumber"
              name="billNumber"
              value={formData.billNumber}
              onChange={onInputChange}
              className="bg-secondary/30"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="billDate">Bill Date</Label>
            <Input
              id="billDate"
              name="billDate"
              type="date"
              value={formData.billDate}
              onChange={onInputChange}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="serviceType">Service Type *</Label>
            <Select
              value={formData.serviceType}
              onValueChange={(value) => onSelectChange('serviceType', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select service type" />
              </SelectTrigger>
              <SelectContent>
                {serviceTypes.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      <Separator />
      
      {/* Customer Info */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Customer Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="customerName">Customer Name *</Label>
            <Input
              id="customerName"
              name="customerName"
              value={formData.customerName}
              onChange={onInputChange}
              placeholder="Enter customer name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customerPhone">Phone Number *</Label>
            <Input
              id="customerPhone"
              name="customerPhone"
              value={formData.customerPhone}
              onChange={onInputChange}
              placeholder="Enter phone number"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="customerAddress">Address</Label>
            <Textarea
              id="customerAddress"
              name="customerAddress"
              value={formData.customerAddress}
              onChange={onInputChange}
              placeholder="Enter address"
              rows={2}
            />
          </div>
        </div>
      </div>
      
      <Separator />
      
      {/* Vehicle Info */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Vehicle Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="vehicleModel">Vehicle Model *</Label>
            <Select
              value={formData.vehicleModel}
              onValueChange={(value) => onSelectChange('vehicleModel', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {vehicleModels.map((model) => (
                  <SelectItem key={model} value={model}>{model}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="vehicleNumber">Vehicle Number</Label>
            <Input
              id="vehicleNumber"
              name="vehicleNumber"
              value={formData.vehicleNumber}
              onChange={onInputChange}
              placeholder="E.g., WB-XX-XX-XXXX"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="chassisNumber">Chassis Number</Label>
            <Input
              id="chassisNumber"
              name="chassisNumber"
              value={formData.chassisNumber}
              onChange={onInputChange}
              placeholder="Enter chassis number"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="kmReading">KM Reading</Label>
            <Input
              id="kmReading"
              name="kmReading"
              value={formData.kmReading}
              onChange={onInputChange}
              placeholder="Current odometer"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="complaints">Customer Complaints</Label>
            <Textarea
              id="complaints"
              name="complaints"
              value={formData.complaints}
              onChange={onInputChange}
              placeholder="Describe issues reported by customer"
              rows={2}
            />
          </div>
        </div>
      </div>
      
      <Separator />
      
      {/* Service Items */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-foreground">Service Work</h3>
          <Button variant="outline" size="sm" onClick={addServiceItem} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Item
          </Button>
        </div>
        <div className="space-y-3">
          {formData.serviceItems.map((item, index) => (
            <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-secondary/20 rounded-lg">
              <div className="col-span-12 md:col-span-5 space-y-1">
                <Label className="text-xs">Description</Label>
                <Input
                  value={item.description}
                  onChange={(e) => onServiceItemChange(index, 'description', e.target.value)}
                  placeholder="Service description"
                />
              </div>
              <div className="col-span-4 md:col-span-2 space-y-1">
                <Label className="text-xs">Qty</Label>
                <Input
                  onKeyDown={handleDigitsKeyDown}
                  inputMode="numeric"
                  value={item.quantity}
                  onChange={(e) => onServiceItemChange(index, 'quantity', e.target.value)}
                  placeholder="1"
                />
              </div>
              <div className="col-span-4 md:col-span-2 space-y-1">
                <Label className="text-xs">Rate</Label>
                <Input
                  onKeyDown={handlePriceKeyDown}
                  inputMode="decimal"
                  value={item.rate}
                  onChange={(e) => onServiceItemChange(index, 'rate', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="col-span-3 md:col-span-2 space-y-1">
                <Label className="text-xs">Amount</Label>
                <Input
                  value={item.amount}
                  readOnly
                  className="bg-secondary/50"
                />
              </div>
              <div className="col-span-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeServiceItem(index)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={formData.serviceItems.length === 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <Separator />
      
      {/* Spare Parts */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-foreground">Spare Parts</h3>
          <Button variant="outline" size="sm" onClick={addPartsItem} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Part
          </Button>
        </div>
        <div className="space-y-3">
          {formData.partsItems.map((item, index) => (
            <div key={index} className="grid grid-cols-12 gap-2 items-end p-3 bg-secondary/20 rounded-lg">
              <div className="col-span-12 md:col-span-3 space-y-1">
                <Label className="text-xs">Part Name</Label>
                <Input
                  value={item.partName}
                  onChange={(e) => onPartsItemChange(index, 'partName', e.target.value)}
                  placeholder="Part name"
                />
              </div>
              <div className="col-span-6 md:col-span-2 space-y-1">
                <Label className="text-xs">Part No.</Label>
                <Input
                  value={item.partNumber}
                  onChange={(e) => onPartsItemChange(index, 'partNumber', e.target.value)}
                  placeholder="Part no."
                />
              </div>
              <div className="col-span-3 md:col-span-2 space-y-1">
                <Label className="text-xs">Qty</Label>
                <Input
                  onKeyDown={handleDigitsKeyDown}
                  inputMode="numeric"
                  value={item.quantity}
                  onChange={(e) => onPartsItemChange(index, 'quantity', e.target.value)}
                  placeholder="1"
                />
              </div>
              <div className="col-span-3 md:col-span-2 space-y-1">
                <Label className="text-xs">Rate</Label>
                <Input
                  onKeyDown={handlePriceKeyDown}
                  inputMode="decimal"
                  value={item.rate}
                  onChange={(e) => onPartsItemChange(index, 'rate', e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="col-span-4 md:col-span-2 space-y-1">
                <Label className="text-xs">Amount</Label>
                <Input
                  value={item.amount}
                  readOnly
                  className="bg-secondary/50"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removePartsItem(index)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={formData.partsItems.length === 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <Separator />
      
      {/* Additional Charges */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Additional Charges & Tax</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="laborCharges">Labor Charges</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="laborCharges"
                name="laborCharges"
                onKeyDown={handlePriceKeyDown}
                inputMode="decimal"
                value={formData.laborCharges}
                onChange={onInputChange}
                placeholder="0"
                className="pl-9"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cgst">CGST</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="cgst"
                name="cgst"
                onKeyDown={handlePriceKeyDown}
                inputMode="decimal"
                value={formData.cgst}
                onChange={onInputChange}
                placeholder="0"
                className="pl-9"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="sgst">SGST</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="sgst"
                name="sgst"
                onKeyDown={handlePriceKeyDown}
                inputMode="decimal"
                value={formData.sgst}
                onChange={onInputChange}
                placeholder="0"
                className="pl-9"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="discount">Discount</Label>
            <div className="relative">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="discount"
                name="discount"
                type="text"
                inputMode="numeric"
                onWheel={(e) => e.target.blur()}
                value={formData.discount}
                onChange={onInputChange}
                placeholder="0"
                className="pl-9"
              />
            </div>
          </div>
        </div>
        
        {/* Total */}
        <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center justify-between">
            <span className="font-heading font-semibold text-foreground">Grand Total</span>
            <span className="text-2xl font-bold text-primary">
              ₹{formatCurrency(calculateTotal())}
            </span>
          </div>
        </div>
      </div>
      
      <Separator />
      
      {/* Payment & Notes */}
      <div>
        <h3 className="font-heading font-semibold text-foreground mb-4">Payment & Notes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="paymentMode">Payment Mode</Label>
            <Select
              value={formData.paymentMode}
              onValueChange={(value) => onSelectChange('paymentMode', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select payment mode" />
              </SelectTrigger>
              <SelectContent>
                {paymentModes.map((mode) => (
                  <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="technicianName">Technician Name</Label>
            <Input
              id="technicianName"
              name="technicianName"
              value={formData.technicianName}
              onChange={onInputChange}
              placeholder="Name of service technician"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={onInputChange}
              placeholder="Service recommendations, next service date, etc."
              rows={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceBillForm;
